import { useEffect, useRef, useState } from "react";

import Collection from "ol/Collection";
import Draw from "ol/interaction/Draw";
import Modify from "ol/interaction/Modify";
import Snap from "ol/interaction/Snap";
import Translate from "ol/interaction/Translate";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";

import * as TEST_UTIL from "../testUtil";
import { ConvertGeometryToGeoJson, GetEditVolumeStyle, JumpToFeature } from "../map/mapUtil";
import { useUserAuth } from "../contexts/authContext";
import { Point } from "ol/geom";
import Polygon, { circular } from "ol/geom/Polygon";
import { ConvertFeetToMeters } from "../util";

export default function useCuasDrawing(map, drawType = "Polygon", existingFeature = null, radius, setRadius) {
    const [featureCoords, setFeatureCoords] = useState([]);
    const [oldDrawType, setOldDrawType] = useState(drawType);
    const featureRef = useRef(null);
    const layerRef = useRef(null);

    // Map interactions used:
    const drawRef = useRef(null);
    const editRef = useRef(null);
    const moveRef = useRef(null);
    const snapRef = useRef(null);

    const { userMapSettings } = useUserAuth();

    useEffect(() => {
        // Should only be called in a testing environment -- set test coordinates and return.
        if (map && map.get("name") === "test") {
            setFeatureCoords(TEST_UTIL.testingCoordinates);
            return;
        }

        // Ensure old map data is removed.
        handleRemoval();

        // Check if the draw type is different.
        // This indicates you want to draw a new feature while currently editing an existing feature.
        const isNewDrawType = oldDrawType !== drawType;
        setOldDrawType(drawType);

        if (!map || drawType === "Clear") {
            return;
        } else if (drawType === "Upload") {
            if (existingFeature) {
                handleEdit(existingFeature.clone());
            }
        } else if (!existingFeature || isNewDrawType) {
            handleDraw();
        } else {
            handleEdit(existingFeature.clone());
        }

        return () => {
            handleRemoval();
        };
    }, [map, drawType, existingFeature]);

    useEffect(() => {
        if (!map || !radius || !featureRef.current) {
            return;
        }

        const geometry = featureRef.current.getGeometry();
        if (!geometry || !(geometry instanceof Polygon)) {
            return;
        }

        const center = geometry.getInteriorPoint().getCoordinates();
        const radiusM = ConvertFeetToMeters(radius);

        const circle = circular(center, radiusM, 128);
        circle.set("type", "Circle");
        featureRef.current.setGeometry(circle);
        setFeatureCoords(ConvertGeometryToGeoJson(circle).coordinates);
    }, [map, radius]);

    const handleDraw = () => {
        drawRef.current = new Draw({ source: new VectorSource({ wrapX: false }), type: drawType === "Circle" ? "Point" : "Polygon" });
        drawRef.current.on("drawend", (event) => handleEdit(event.feature));
        map.addInteraction(drawRef.current);
    };

    const handleEdit = (feature) => {
        if (drawRef.current) {
            map.removeInteraction(drawRef.current);
        }

        let geometry = feature.getGeometry();
        if (geometry instanceof Point) {
            geometry = circular(geometry.getCoordinates(), ConvertFeetToMeters(1000), 128);
            geometry.set("type", "Circle");
            feature.set("type", "Circle");
            feature.setGeometry(geometry);
            setRadius(1000);
        }

        JumpToFeature(map, feature);
        setFeatureCoords(ConvertGeometryToGeoJson(geometry).coordinates);

        const features = new Collection([feature]);
        featureRef.current = feature;
        layerRef.current = new VectorLayer({ source: new VectorSource({ features }), style: GetEditVolumeStyle(userMapSettings), zIndex: 1 });
        snapRef.current = new Snap({ features });
        moveRef.current = new Translate({ filter: (f) => f === feature });
        moveRef.current.on("translateend", (event) => updateCoordinates(event));

        if (feature.get("type") !== "Circle") {
            editRef.current = new Modify({ features, deleteCondition: (event) => event.originalEvent.button === 2 });
            editRef.current.on("modifyend", (event) => updateCoordinates(event));
            map.addInteraction(editRef.current);
        }

        const updateCoordinates = (event) => {
            if (event.features.getLength() > 0) {
                setFeatureCoords(ConvertGeometryToGeoJson(feature.getGeometry()).coordinates);
            }
        };

        map.addLayer(layerRef.current);
        map.addInteraction(moveRef.current);
        map.addInteraction(snapRef.current);
    };

    const handleRemoval = () => {
        setFeatureCoords([]);
        if (map && layerRef.current) map.removeLayer(layerRef.current);
        if (map && drawRef.current) map.removeInteraction(drawRef.current);
        if (map && editRef.current) map.removeInteraction(editRef.current);
        if (map && moveRef.current) map.removeInteraction(moveRef.current);
        if (map && snapRef.current) map.removeInteraction(snapRef.current);
        featureRef.current = null;
        layerRef.current = null;
        drawRef.current = null;
        editRef.current = null;
        moveRef.current = null;
        snapRef.current = null;
    };

    return featureCoords;
}
